"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { EcommerceProductsProvider, useEcommerceProducts, AddToCartButton } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { ArrowRight, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
const wp_query_params = {
  per_page: 4,
  status: "publish",
  order: "desc",
  orderby: "date"
};
const wp_query = new WP_Query(wp_query_params);
function FeaturedProductsConsumer() {
  const {
    products,
    loading,
    error,
    refetch
  } = useEcommerceProducts();
  if (loading) {
    return <div data-section-id="58020" className="flex h-96 w-full items-center justify-center">
        <Loader2 className="text-ring h-10 w-10 animate-spin" />
      </div>;
  }
  if (error) {
    return <div className="flex h-64 w-full flex-col items-center justify-center text-center">
        <p className="text-muted-foreground mb-4">Unable to load project data.</p>
        <button onClick={refetch} className="text-ring hover:underline font-default">
          Try Again
        </button>
      </div>;
  }
  if (!products || products.length === 0) {
    return null;
  }
  return <div className="flex flex-col gap-24 md:gap-32">
      {products.map((product, i) => {
      const isEven = i % 2 === 0;
      return <article key={product.id} data-index={i} className={cn("group flex flex-col gap-8 md:items-center lg:gap-16", isEven ? "md:flex-row" : "md:flex-row-reverse")}>
            {/* Left Column: Drone Imagery */}
            <div className="relative w-full flex-1">
              <div className="bg-card/5 shadow-primary/10 relative aspect-[4/5] overflow-hidden rounded-none border border-border/50 shadow-2xl transition-all duration-500 group-hover:shadow-3xl md:aspect-square lg:aspect-[4/3]">
                {/* BIM Wireframe & Cool Tone Overlay */}
                <div className="absolute inset-0 bg-blue-900/20 mix-blend-overlay opacity-40" />
                <div className="absolute inset-0 bg-gradient-to-br from-primary/30 via-transparent to-transparent opacity-60" />
                
                {product.featured_image_url ? <img src={product.featured_image_url} alt={product.title} className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105" loading="lazy" /> : <div className="flex h-full w-full items-center justify-center bg-muted text-muted-foreground">
                    Project Visual Pending
                  </div>}

                {/* Blueprint Accent Ring */}
                <div className="pointer-events-none absolute inset-0 rounded-none ring-1 ring-ring/30 ring-inset" />
                
                {/* View Case Study Overlay */}
                <div className="absolute inset-0 flex items-center justify-center bg-primary/80 opacity-0 backdrop-blur-sm transition-opacity duration-300 group-hover:opacity-100">
                  <Link to={product.link} className="text-primary-foreground flex items-center gap-2 font-default font-bold tracking-widest uppercase">
                    View Case Study <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>
              </div>
            </div>

            {/* Right Column: Project Details */}
            <div className="flex w-full flex-1 flex-col justify-center text-left">
              <div className="space-y-6">
                {/* Project Label */}
                <div className="font-mono text-ring flex items-center gap-4 text-sm tracking-wider uppercase">
                  <span>PROJECT 0{i + 1}</span>
                  <span className="h-px w-8 bg-ring opacity-50" />
                  <span className="text-lg font-bold">
                    INFRASTRUCTURE
                  </span>
                </div>

                {/* Project Title */}
                <h2 className="font-default text-foreground text-4xl font-extrabold leading-[0.9] tracking-tighter uppercase md:text-5xl lg:text-6xl xl:text-[4rem]">
                  <Link to={product.link} className="hover:text-ring transition-colors">
                    {product.title}
                  </Link>
                </h2>

                {/* Project Brief */}
                {product.short_description && <div className="text-muted-foreground prose prose-sm max-w-md font-default font-medium leading-relaxed" dangerouslySetInnerHTML={{
              __html: product.short_description
            }} />}

                {/* Call to Action */}
                <div className="pt-4">
                  <AddToCartButton productId={product.id} variant="default" size="lg" className="w-full sm:w-auto" buttonClassName="bg-accent text-accent-foreground hover:bg-accent/80 h-14 px-8 rounded-none border-l-4 border-ring text-base font-bold tracking-wide uppercase transition-all shadow-md" text="Explore Project" showQuantity={false} />
                </div>
              </div>
            </div>
          </article>;
    })}
    </div>;
}
export default function FeaturedProducts() {
  return <section id="project-1-overview" className="bg-background text-foreground relative overflow-hidden py-24 md:py-32">
      <div className="container mx-auto px-4 md:px-6">
        <EcommerceProductsProvider wp_query={{
        per_page: 4,
        status: ["publish"],
        order: "desc",
        orderby: "date"
      }} className="w-full">
          <FeaturedProductsConsumer />
        </EcommerceProductsProvider>
      </div>
    </section>;
}